/** Self-service recovery. The acknowledgement never reveals account existence. */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Mail } from 'lucide-react';
import { Button, Callout, Dialog, Field, Input } from '@/components/ui';
import { ApiError, request } from '@/lib/api';

export interface ResetAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialEmail?: string;
  /**
   * Where focus goes when the dialog closes. Required from the caller: this
   * dialog is opened through a controlled `open` prop rather than a
   * `DialogTrigger`, so Radix has no trigger node to restore to — see the
   * docblock on `Dialog`.
   */
  onCloseAutoFocus?: (event: Event) => void;
}

export function ResetAccessDialog({
  open,
  onOpenChange,
  initialEmail = '',
  onCloseAutoFocus,
}: ResetAccessDialogProps) {
  const [email, setEmail] = useState(initialEmail);
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const requestVersion = useRef(0);
  useEffect(() => {
    requestVersion.current++;
    setPending(false);
    if (open) {
      setEmail(initialEmail);
      setSent(false);
      setError(null);
      setFieldError(null);
    }
  }, [open, initialEmail]);
  useEffect(() => { if (sent) statusRef.current?.focus(); }, [sent]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setFieldError('Enter the email address you use to sign in.');
      document.getElementById('af-recovery-email')?.focus();
      return;
    }
    setPending(true);
    const version = requestVersion.current;
    setError(null);
    setFieldError(null);
    try {
      await request('/auth/forgot-password', { method: 'POST', body: { email: email.trim() } });
      if (version === requestVersion.current) setSent(true);
    } catch (failure) {
      if (version === requestVersion.current) setError(ApiError.is(failure) && failure.status === 429
        ? 'Too many requests. Wait a little before trying again.'
        : 'We could not request a link. Check your connection and try again.');
    } finally {
      if (version === requestVersion.current) setPending(false);
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        document.getElementById('af-recovery-email')?.focus();
      }}
      onCloseAutoFocus={onCloseAutoFocus}
      title="Reset your password"
      description={sent ? 'Check your email for the next step.' : 'Enter the email address you use to sign in. We will email a link so you can choose a new password.'}
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <Button variant="ghost" size="md" onClick={() => onOpenChange(false)}>
            Back to sign in
          </Button>
          {!sent && <Button
            type="submit"
            form="af-recovery-form"
            variant="primary"
            size="md"
            className="font-semibold"
            iconLeft={<Mail aria-hidden="true" />}
            loading={pending}
            loadingLabel="Requesting link…"
          >
            Send reset link
          </Button>}
        </div>
      }
    >
      {sent ? (
        <p ref={statusRef} role="status" tabIndex={-1} className="text-sm leading-normal text-secondary">
          If an active account uses that address, we will email a reset link. Check your inbox and spam folder. The link works once and expires in 15 minutes.
        </p>
      ) : (
        <form id="af-recovery-form" onSubmit={submit} noValidate>
          {error && <Callout variant="danger" className="mb-4">{error}</Callout>}
          <Field label="Email" htmlFor="af-recovery-email" error={fieldError}>
            {({ id, describedBy, invalid }) => <Input id={id} type="email" name="email" required autoComplete="email" autoCapitalize="none" spellCheck={false} maxLength={254} size="lg" iconLeft={<Mail />} value={email} invalid={invalid} aria-describedby={describedBy} disabled={pending} onChange={(event) => setEmail(event.target.value)} />}
          </Field>
        </form>
      )}
    </Dialog>
  );
}
